/**
 * AccessDeniedRedirect
 *
 * Renders a `<Navigate>` to `/access-denied?return=<currentPath>` so a
 * route-level RBAC guard's denial surfaces as the editorial 403 page
 * (info-pages V3 — `PermissionDeniedPage`) instead of a silent bounce
 * back to the overview.
 *
 * Used as the default failure branch in `ModulePageGuard`,
 * `CompanyAccessGate`, and `TreasuryAccessGate`. Guards that want the
 * old "bounce to a safe overview" behavior keep using their explicit
 * `fallbackTo` prop and pass a regular `<Navigate />` instead.
 *
 * Encodes path + search + hash so a deep-link with query params and a
 * fragment round-trips cleanly through the editorial 403 surface (the
 * page itself doesn't read the return param yet — that's a follow-up
 * for V3's "request access" flow — but the round-trip is preserved
 * upstream).
 */

import { Navigate, useLocation } from 'react-router-dom';

export interface AccessDeniedRedirectProps {
  /**
   * Optional missing-permission codename to surface on the editorial 403
   * page (e.g. `location.settle_obligations`). When set it renders as a
   * mono diagnostic pill alongside the body copy. Guards that know which
   * permission failed should pass this; guards that only have a boolean
   * `allowed` flag can omit it and the page renders the generic copy.
   */
  missing?: string;
  /**
   * Optional permission scope label (e.g. a location name or company
   * scope) — pairs with `missing` in the diagnostic block.
   */
  scope?: string;
}

export function AccessDeniedRedirect({ missing, scope }: AccessDeniedRedirectProps = {}) {
  const location = useLocation();
  // URLSearchParams handles encoding so we pass the raw URL string —
  // no double-encoding when the page later decodes ?return=.
  const params = new URLSearchParams({
    return: `${location.pathname}${location.search}${location.hash}`,
  });
  if (missing) params.set('missing', missing);
  if (scope) params.set('scope', scope);
  return <Navigate to={`/access-denied?${params.toString()}`} replace />;
}
